import type OLMap from 'ol/Map';
import type BaseLayer from 'ol/layer/Base';
import type Layer from 'ol/layer/Layer';
import { el } from './dom';

type RenderEvent = { context?: CanvasRenderingContext2D | WebGLRenderingContext };

/**
 * Swipe divider: the top layer is clipped to the right of the handle, so the two
 * newest layers in the stack can be read against each other on the same spot.
 */
export class Compare {
  readonly handle = el('div', { class: 'compare-handle', attrs: { hidden: 'hidden' } }, [
    el('div', { class: 'compare-line' }),
    el('div', { class: 'compare-grab', text: '⇤⇥' }),
  ]);

  private fraction = 0.5;
  private layer?: Layer;
  private readonly onPre = (ev: unknown) => {
    const ctx = (ev as RenderEvent).context;
    if (!(ctx instanceof CanvasRenderingContext2D)) return;
    const width = ctx.canvas.width;
    const x = width * this.fraction;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, 0, width - x, ctx.canvas.height);
    ctx.clip();
  };
  private readonly onPost = (ev: unknown) => {
    const ctx = (ev as RenderEvent).context;
    if (ctx instanceof CanvasRenderingContext2D) ctx.restore();
  };

  constructor(private readonly map: OLMap) {
    this.handle.addEventListener('pointerdown', this.onDown);
    this.position();
  }

  enable(layer: BaseLayer | undefined): void {
    this.disable();
    if (!layer || !('on' in layer)) return;
    this.layer = layer as Layer;
    this.layer.on('prerender', this.onPre);
    this.layer.on('postrender', this.onPost);
    this.handle.hidden = false;
    this.position();
    this.map.render();
  }

  disable(): void {
    if (this.layer) {
      this.layer.un('prerender', this.onPre);
      this.layer.un('postrender', this.onPost);
      this.layer = undefined;
      this.map.render();
    }
    this.handle.hidden = true;
  }

  private position(): void {
    this.handle.style.left = `${this.fraction * 100}%`;
  }

  private onDown = (ev: PointerEvent): void => {
    this.handle.setPointerCapture(ev.pointerId);
    this.handle.addEventListener('pointermove', this.onMove);
    this.handle.addEventListener('pointerup', this.onUp);
    this.handle.addEventListener('pointercancel', this.onUp);
    ev.preventDefault();
  };

  private onMove = (ev: PointerEvent): void => {
    const rect = this.map.getTargetElement().getBoundingClientRect();
    this.fraction = Math.min(0.98, Math.max(0.02, (ev.clientX - rect.left) / rect.width));
    this.position();
    this.map.render();
  };

  private onUp = (ev: PointerEvent): void => {
    this.handle.releasePointerCapture(ev.pointerId);
    this.handle.removeEventListener('pointermove', this.onMove);
    this.handle.removeEventListener('pointerup', this.onUp);
    this.handle.removeEventListener('pointercancel', this.onUp);
  };
}
